'use no memo';
'use client';
import {
  type Cell,
  type ColumnDef,
  flexRender,
  type Row,
  type Table as TableType,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/table';
import { cn } from '../../utils';

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

function useIsTabletOrMobile(): boolean {
  const [isTabletOrMobile, setIsTabletOrMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    setIsTabletOrMobile(mq.matches);
    const handler = () => setIsTabletOrMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isTabletOrMobile;
}

type DataTableProps<TData> = {
  table: TableType<TData>;
  showHeader?: boolean;
  /** Extra Tailwind classes merged onto the sticky header row (e.g. 'shadow-none'). */
  headerClassName?: string;
  enableMobileLayout?: boolean;
  /** Render rows as cards on tablets and mobile (≤1024px). */
  useMobileCard?: boolean;
  /** Always render rows as cards regardless of screen width. */
  alwaysCard?: boolean;
  /** Extra Tailwind classes applied to each card when in mobile card mode. */
  mobileCardClassName?: string;
  // New accordion props
  enableAccordion?: boolean;
  accordionContent?: (row: Row<TData>) => React.ReactNode;
  accordionIcon?: React.ComponentType<{ isExpanded: boolean }>;
  accordionIconPosition?: 'start' | 'end';
  // Custom accordion props
  accordionProps?: {
    className?: string;
    contentClassName?: string;
    iconClassName?: string;
    animationDuration?: number;
    easing?: string;
    showIconInColumn?: boolean; // New prop to control icon placement
  };
} & Omit<TableRowComponentProps<TData>, 'row'>;

/**
 * Where a column goes on a phone card, set as `meta: { mobileCard }` on its column definition:
 * - `title`: the card's heading;
 * - `headline`: beside the heading, for the figure people scan for (a utilization, a status);
 * - `detail`: behind the card's "More" disclosure.
 * Columns without it sit in the card's body. A table that sets it on no column keeps the
 * original card layout, where every column is a label and value in order.
 */
export type DataTableMobileCardPlacement = 'title' | 'headline' | 'detail';

/** Reads `meta.mobileCard`. Apps declare it on their own `ColumnMeta` to type their columns. */
const getPlacement = <TData,>(columnDef: ColumnDef<TData, unknown>) =>
  (columnDef.meta as { mobileCard?: DataTableMobileCardPlacement } | undefined)?.mobileCard;

const headerLabel = <TData,>(table: TableType<TData>, cell: Cell<TData, unknown>) => {
  const header = table
    .getHeaderGroups()
    .flatMap((group) => group.headers)
    .find((h) => h.column.id === cell.column.id);
  return header && !header.isPlaceholder
    ? flexRender(header.column.columnDef.header, header.getContext())
    : null;
};

/** One label-and-value line on a placed card: the label in a fixed column, the value beside it. */
const PlacedCardField = <TData,>({
  table,
  cell,
}: {
  table: TableType<TData>;
  cell: Cell<TData, unknown>;
}) => (
  <div className="flex items-start gap-4 text-sm">
    <span className="text-muted-foreground w-32 shrink-0">{headerLabel(table, cell)}</span>
    <span className="min-w-0 flex-1 break-words">
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </span>
  </div>
);

/** Two or three figures sit side by side on a placed row; more than that, one per line. */
const placedFigureColumns: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
};

/**
 * One record on a phone, laid out by its columns' placements: the title with the headline at its
 * right, the other figures side by side (label over value), and the details behind More. Rows are
 * divided by a rule, not boxed, like the rest of a phone record.
 */
const PlacedCard = <TData,>({
  table,
  row,
  isClickable,
  onClick,
  className,
}: {
  table: TableType<TData>;
  row: Row<TData>;
  isClickable: boolean;
  onClick?: () => void;
  className?: string;
}) => {
  const [showDetail, setShowDetail] = useState(false);
  const cells = row.getVisibleCells();
  const placed = (placement?: DataTableMobileCardPlacement) =>
    cells.filter((cell) => getPlacement(cell.column.columnDef) === placement);
  const titleCells = placed('title');
  const headlineCells = placed('headline');
  // A detail column with an accessor and no value for this row is left out, so a card with
  // nothing more to show has no More.
  const detailCells = placed('detail').filter(
    (cell) => !cell.column.accessorFn || cell.getValue() !== undefined,
  );
  const actionCell = cells.find((cell) => cell.column.id === 'actions');
  const bodyCells = placed(undefined).filter((cell) => cell.column.id !== 'actions');
  const figureColumns = placedFigureColumns[bodyCells.length];

  return (
    <div
      className={cn(
        'flex flex-col gap-2 py-3',
        isClickable && 'active:bg-muted cursor-pointer',
        className,
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 break-words text-sm font-semibold">
          {titleCells.map((cell) => (
            <span key={cell.id} className="block">
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </span>
          ))}
        </div>
        {headlineCells.length > 0 && (
          <div className="flex shrink-0 flex-col items-end gap-1 text-sm">
            {headlineCells.map((cell) => (
              <span key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
            ))}
          </div>
        )}
      </div>
      {figureColumns ? (
        <dl className={cn('grid gap-3', figureColumns)}>
          {bodyCells.map((cell) => (
            <div key={cell.id} className="flex min-w-0 flex-col">
              <dt className="text-muted-foreground text-xs">{headerLabel(table, cell)}</dt>
              <dd className="break-words text-sm">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        bodyCells.length > 0 && (
          <div className="flex flex-col gap-1">
            {bodyCells.map((cell) => (
              <PlacedCardField key={cell.id} table={table} cell={cell} />
            ))}
          </div>
        )
      )}
      {detailCells.length > 0 && (
        <div className="flex flex-col gap-1">
          {showDetail &&
            detailCells.map((cell) => <PlacedCardField key={cell.id} table={table} cell={cell} />)}
          <button
            type="button"
            aria-expanded={showDetail}
            className="text-muted-foreground touch:-my-3 touch:min-h-11 hover:text-foreground flex w-fit items-center gap-1 text-sm font-medium"
            onClick={(event) => {
              event.stopPropagation();
              setShowDetail((open) => !open);
            }}
          >
            {showDetail ? 'Less' : 'More'}
            {showDetail ? (
              <ChevronUp className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronDown className="h-4 w-4" aria-hidden />
            )}
          </button>
        </div>
      )}
      {actionCell && (
        <div className="flex justify-end">
          {flexRender(actionCell.column.columnDef.cell, actionCell.getContext())}
        </div>
      )}
    </div>
  );
};

/** Phone rows laid out by each column's `mobileCard` placement. */
const DataTablePlacedCards = <TData,>({
  table,
  onRowClick,
  shouldApplyOnRowClick,
  mobileCardClassName,
}: Pick<DataTableProps<TData>, 'table' | 'mobileCardClassName'> &
  Pick<TableRowComponentProps<TData>, 'onRowClick' | 'shouldApplyOnRowClick'>) => {
  const rows = table.getRowModel().rows;
  if (rows.length === 0) {
    return <div className="text-muted-foreground py-8 text-center text-sm">No results.</div>;
  }
  return (
    <div className="divide-border flex flex-col divide-y">
      {rows.map((row) => {
        const isClickable =
          !!onRowClick && (shouldApplyOnRowClick ? shouldApplyOnRowClick(row.original) : true);
        return (
          <PlacedCard
            key={row.id}
            table={table}
            row={row}
            isClickable={isClickable}
            onClick={isClickable ? () => onRowClick?.(row.original) : undefined}
            className={mobileCardClassName}
          />
        );
      })}
    </div>
  );
};

const DataTableMobileCards = <TData,>({
  table,
  onRowClick,
  shouldApplyOnRowClick,
  mobileCardClassName,
}: Pick<DataTableProps<TData>, 'table' | 'mobileCardClassName'> &
  Pick<TableRowComponentProps<TData>, 'onRowClick' | 'shouldApplyOnRowClick'>) => {
  const headers = table.getHeaderGroups()[0]?.headers ?? [];

  return (
    <div className="space-y-3 p-3">
      {table.getRowModel().rows.length ? (
        table.getRowModel().rows.map((row) => {
          const cells = row.getVisibleCells();
          const [firstCell, ...remainingCells] = cells;
          const [, ...remainingHeaders] = headers;

          const isClickable =
            onRowClick && (shouldApplyOnRowClick ? shouldApplyOnRowClick(row.original) : true);

          return (
            <Card
              key={row.id}
              className={cn(
                'overflow-hidden border border-stone-300 bg-white shadow-sm',
                isClickable && 'cursor-pointer',
                mobileCardClassName,
              )}
              onClick={isClickable ? () => onRowClick(row.original) : undefined}
            >
              <CardHeader className="border-b border-stone-300 bg-stone-50 pb-2 pt-3">
                <CardTitle className="break-words text-sm font-semibold">
                  {flexRender(firstCell.column.columnDef.cell, firstCell.getContext())}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                {(() => {
                  const dataCells = remainingCells.filter((c) => c.column.id !== 'actions');
                  const actionCell = remainingCells.find((c) => c.column.id === 'actions');
                  const useGrid = dataCells.length > 5;

                  return (
                    <>
                      <div
                        className={useGrid ? 'grid grid-cols-2 gap-x-3 gap-y-1.5' : 'space-y-1.5'}
                      >
                        {dataCells.map((cell, idx) => {
                          const header = remainingHeaders[idx];
                          const headerContent =
                            header && !header.isPlaceholder
                              ? flexRender(header.column.columnDef.header, header.getContext())
                              : null;

                          return useGrid ? (
                            <div key={cell.id} className="flex min-w-0 flex-col gap-0.5 text-xs">
                              <span className="text-muted-foreground break-words font-medium leading-none">
                                {headerContent}
                              </span>
                              <span className="min-w-0 break-words text-neutral-800 [overflow-wrap:anywhere]">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </span>
                            </div>
                          ) : (
                            <div key={cell.id} className="flex items-start gap-2 text-xs">
                              <span className="text-muted-foreground w-24 shrink-0 font-medium">
                                {headerContent}
                              </span>
                              <span className="min-w-0 flex-1 break-words text-neutral-800 [overflow-wrap:anywhere]">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {actionCell ? (
                        <div className="flex justify-end pt-2">
                          {flexRender(actionCell.column.columnDef.cell, actionCell.getContext())}
                        </div>
                      ) : null}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          );
        })
      ) : (
        <div className="text-muted-foreground py-8 text-center text-sm">No results.</div>
      )}
    </div>
  );
};

export const DataTable = <TData,>({
  table,
  showHeader = true,
  headerClassName,
  enableMobileLayout = false,
  useMobileCard = false,
  alwaysCard = false,
  mobileCardClassName,
  enableAccordion = false,
  enableColumnSizing = false,
  accordionContent,
  accordionIcon: CustomAccordionIcon,
  accordionIconPosition = 'end',
  accordionProps = {},
  ...rowProps
}: DataTableProps<TData>) => {
  const isMobile = useIsMobile();
  const isTabletOrMobile = useIsTabletOrMobile();

  if (alwaysCard || (enableMobileLayout && isMobile) || (useMobileCard && isTabletOrMobile)) {
    const usesPlacement = table
      .getAllLeafColumns()
      .some((column) => getPlacement(column.columnDef) !== undefined);
    if (usesPlacement) {
      return (
        <DataTablePlacedCards
          table={table}
          mobileCardClassName={mobileCardClassName}
          onRowClick={rowProps.onRowClick}
          shouldApplyOnRowClick={rowProps.shouldApplyOnRowClick}
        />
      );
    }
    return (
      <DataTableMobileCards
        table={table}
        mobileCardClassName={mobileCardClassName}
        onRowClick={rowProps.onRowClick}
        shouldApplyOnRowClick={rowProps.shouldApplyOnRowClick}
      />
    );
  }

  // Column sizes act as proportions: each column takes size/total of the width, so the table fills
  // wide containers and scrolls below the summed min width — and a fr-based grid can match it exactly.
  const totalColumnSize = enableColumnSizing
    ? table.getAllLeafColumns().reduce((sum, col) => sum + col.getSize(), 0)
    : 0;

  return (
    <Table
      className={cn('relative w-full', enableColumnSizing && 'table-fixed')}
      style={totalColumnSize ? { minWidth: totalColumnSize } : undefined}
    >
      {showHeader && (
        <TableHeader className={cn('bg-background sticky top-0 z-10 shadow', headerClassName)}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    style={
                      enableColumnSizing
                        ? { width: `${(header.getSize() / totalColumnSize) * 100}%` }
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
      )}
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table
            .getRowModel()
            .rows.map((row) => (
              <TableRowComponent
                key={row.id}
                row={row}
                enableColumnSizing={enableColumnSizing}
                enableAccordion={enableAccordion}
                accordionContent={accordionContent}
                accordionIcon={CustomAccordionIcon}
                accordionIconPosition={accordionIconPosition}
                accordionProps={accordionProps}
                {...rowProps}
              />
            ))
        ) : (
          <TableRow>
            <TableCell colSpan={table.getAllColumns().length} className="h-24 text-center">
              No results.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

type TableRowComponentProps<TData> = {
  row: Row<TData>;
  onRowClick?: (entry: TData, nativeEvent?: React.MouseEvent<HTMLTableRowElement>) => void;
  shouldApplyOnRowClick?: (entry: TData) => boolean;
  rowClassName?: (row: Row<TData>) => string | undefined;
  tableCellClassName?: string;
  RowWrapper?: React.ComponentType<{ row: Row<TData>; children: React.ReactNode }>;
  /** Apply each column's `size` as a fixed width (opt-in); no effect unless columns set a size. */
  enableColumnSizing?: boolean;
  /** Always-visible full-width row rendered beneath a row; return null to skip that row. */
  subRowContent?: (row: Row<TData>) => React.ReactNode;
  // Accordion props
  enableAccordion?: boolean;
  accordionContent?: (row: Row<TData>) => React.ReactNode;
  /** Optional per-row gate: return false to render a row with no expander (no toggle, no expand). */
  canExpandRow?: (row: Row<TData>) => boolean;
  accordionIcon?: React.ComponentType<{ isExpanded: boolean }>;
  accordionIconPosition?: 'start' | 'end';
  accordionProps?: {
    className?: string;
    contentClassName?: string;
    iconClassName?: string;
    animationDuration?: number;
    easing?: string;
    showIconInColumn?: boolean;
  };
};

const TableRowComponent = <TData,>({
  row,
  onRowClick,
  shouldApplyOnRowClick,
  rowClassName,
  tableCellClassName,
  RowWrapper,
  enableColumnSizing = false,
  subRowContent,
  enableAccordion = false,
  accordionContent,
  canExpandRow,
  accordionIcon: CustomAccordionIcon,
  accordionIconPosition = 'end',
  accordionProps = {},
}: TableRowComponentProps<TData>) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);

  const {
    className = '',
    contentClassName = '',
    iconClassName = '',
    animationDuration = 300,
    easing = 'ease-in-out',
    showIconInColumn = false, // Default to false for better UX
  } = accordionProps;

  // canExpandRow further gates the opt-in accordion per row; defaults to expandable.
  const canExpand =
    enableAccordion && !!accordionContent && (canExpandRow ? canExpandRow(row) : true);

  const isOnRowClickApplicable = useMemo(() => {
    return shouldApplyOnRowClick ? shouldApplyOnRowClick(row.original) : true;
  }, [row.original, shouldApplyOnRowClick]);

  const onRowClickHandler = useCallback<React.MouseEventHandler<HTMLTableRowElement>>(
    (e) => {
      if (onRowClick && isOnRowClickApplicable) {
        onRowClick(row.original, e);
      }
    },
    [isOnRowClickApplicable, onRowClick, row.original],
  );

  const onAccordionToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isAnimating) return;

      setIsAnimating(true);
      if (isExpanded) {
        // Closing animation
        setIsExpanded(false);
        setTimeout(() => setIsAnimating(false), animationDuration);
      } else {
        // Opening animation
        setIsExpanded(true);
        setTimeout(() => setIsAnimating(false), animationDuration);
      }
    },
    [isExpanded, isAnimating, animationDuration],
  );

  // Measure content height for smooth animation. ResizeObserver tracks size
  // changes *after* the initial expand (e.g. lazy data resolving, nested
  // toggles) so the wrapper height never clips the rendered content.
  useEffect(() => {
    const el = contentRef.current;
    if (!el || !isExpanded) return;
    setContentHeight(el.scrollHeight);
    const observer = new ResizeObserver(() => setContentHeight(el.scrollHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, [isExpanded, accordionContent]);

  const DefaultAccordionIcon = ({ isExpanded }: { isExpanded: boolean }) => {
    return isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />;
  };

  const AccordionIcon = CustomAccordionIcon || DefaultAccordionIcon;

  const subContent = subRowContent?.(row);

  const renderCells = () => {
    const totalSize = enableColumnSizing
      ? row.getVisibleCells().reduce((sum, cell) => sum + cell.column.getSize(), 0)
      : 0;
    const cells = row.getVisibleCells().map((cell) => (
      <TableCell
        className={tableCellClassName}
        style={
          enableColumnSizing
            ? { width: `${(cell.column.getSize() / totalSize) * 100}%` }
            : undefined
        }
        key={cell.id}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    ));

    // Only add icon column if explicitly requested
    if (canExpand && showIconInColumn) {
      if (accordionIconPosition === 'start') {
        return [
          <TableCell key="accordion-icon" className="w-10 p-0">
            <button
              onClick={onAccordionToggle}
              className={cn(
                'hover:bg-muted/50 flex h-full w-full items-center justify-center p-2 transition-colors',
                iconClassName,
              )}
              disabled={isAnimating}
            >
              <AccordionIcon isExpanded={isExpanded} />
            </button>
          </TableCell>,
          ...cells,
        ];
      } else {
        return [
          ...cells,
          <TableCell key="accordion-icon" className="w-10 p-0">
            <button
              onClick={onAccordionToggle}
              className={cn(
                'hover:bg-muted/50 flex h-full w-full items-center justify-center p-2 transition-colors',
                iconClassName,
              )}
              disabled={isAnimating}
            >
              <AccordionIcon isExpanded={isExpanded} />
            </button>
          </TableCell>,
        ];
      }
    }

    return cells;
  };

  const markup = (
    <>
      <TableRow
        onClick={(e) => {
          // Don't trigger accordion if clicking on interactive elements
          if (
            canExpand &&
            !(e.target as HTMLElement).closest('button, a, input, select, textarea')
          ) {
            onAccordionToggle(e);
          } else if (!canExpand && onRowClickHandler) {
            onRowClickHandler(e);
          }
        }}
        className={cn(
          isOnRowClickApplicable && onRowClick && 'cursor-pointer',
          canExpand && 'hover:bg-muted/50 cursor-pointer',
          className,
          rowClassName?.(row),
        )}
        key={row.id}
        data-state={row.getIsSelected() && 'selected'}
      >
        {renderCells()}
      </TableRow>
      {subContent != null && (
        <TableRow
          data-subrow=""
          onClick={onRowClickHandler}
          className={cn(isOnRowClickApplicable && onRowClick && 'cursor-pointer', className)}
        >
          <TableCell
            colSpan={row.getVisibleCells().length + (canExpand && showIconInColumn ? 1 : 0)}
          >
            {subContent}
          </TableCell>
        </TableRow>
      )}
      {canExpand && (
        <>
          {/* Compact toggle button row */}
          <TableRow
            className="hover:bg-muted/30 cursor-pointer"
            style={{ height: '30px', minHeight: '30px' }}
            onClick={onAccordionToggle}
          >
            <TableCell
              colSpan={row.getVisibleCells().length + (showIconInColumn ? 1 : 0)}
              className="p-0"
              style={{ height: '20px', minHeight: '20px', padding: 0 }}
            >
              <div className="flex justify-center px-2 py-0">
                <div className="text-muted-foreground flex items-center gap-1 text-xs">
                  {accordionIconPosition === 'end' ? (
                    <>
                      <span>{isExpanded ? 'Less' : 'More'}</span>
                      <AccordionIcon isExpanded={isExpanded} />
                    </>
                  ) : (
                    <>
                      <AccordionIcon isExpanded={isExpanded} />
                      <span>{isExpanded ? 'Less' : 'More'}</span>
                    </>
                  )}
                </div>
              </div>
            </TableCell>
          </TableRow>

          {/* Expandable content row */}
          <TableRow
            className={isExpanded ? '' : 'h-0 min-h-0'}
            style={!isExpanded ? { height: 0, minHeight: 0, lineHeight: 0 } : {}}
          >
            <TableCell
              colSpan={row.getVisibleCells().length + (showIconInColumn ? 1 : 0)}
              className={cn('p-0', !isExpanded && 'h-0 min-h-0 border-0')}
              style={!isExpanded ? { height: 0, minHeight: 0, padding: 0, border: 'none' } : {}}
            >
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300 ease-in-out',
                  contentClassName,
                )}
                style={{
                  height: isExpanded ? contentHeight : 0,
                  transitionDuration: `${animationDuration}ms`,
                  transitionTimingFunction: easing,
                }}
              >
                <div ref={contentRef} className="bg-muted/30 border-t">
                  {accordionContent(row)}
                </div>
              </div>
            </TableCell>
          </TableRow>
        </>
      )}
    </>
  );

  return RowWrapper ? <RowWrapper row={row}>{markup}</RowWrapper> : markup;
};
